import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from 'react'

// IconButton — square 26×26 button hosting an SVG glyph plus an accessible
// `aria-label`. Used for compact action rows (publish / AI / edit / delete,
// replace / remove GPX, etc.) so every action stays the same visual weight
// and lines up with the surrounding chrome.

export type IconButtonTone = 'default' | 'primary' | 'danger' | 'accent'

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label'
> {
  /** Accessible label; also used as `title` for hover tooltip. */
  label: string
  tone?: IconButtonTone
  children: ReactNode
}

const BASE =
  'inline-flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[var(--xr-radius-md)] border transition ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-[var(--xr-brand-primary)] focus-visible:ring-offset-[var(--xr-bg-canvas)]'

const TONE_CLS: Record<IconButtonTone, string> = {
  default:
    'border-[var(--xr-border-strong)] bg-[var(--xr-bg-surface)] text-[var(--xr-text-primary)] hover:bg-[var(--xr-bg-surface-elev)]',
  primary:
    'border-transparent bg-[var(--xr-brand-primary)] text-[var(--xr-brand-primary-ink)] hover:bg-[var(--xr-brand-primary-hover)]',
  danger:
    'border-[var(--xr-danger)] bg-[var(--xr-bg-surface)] text-[var(--xr-danger)] hover:bg-[var(--xr-danger-soft)]',
  accent:
    'border-[var(--xr-accent)] bg-[var(--xr-bg-surface)] text-[var(--xr-accent)] hover:bg-[var(--xr-accent-soft)]',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tone = 'default', className = '', children, ...rest },
  ref,
) {
  const cls = `${BASE} ${TONE_CLS[tone]} ${className}`.trim()
  return (
    <button ref={ref} type="button" aria-label={label} title={label} className={cls} {...rest}>
      {children}
    </button>
  )
})

// ─── glyph SVGs ───────────────────────────────────────────────────────────
// All sized to 16x16, currentColor stroke, 1.75 weight. Match the iconography
// already used by ThemeToggle so the visual language stays consistent.

export function IconArrowUp() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 20V6" />
      <path d="m5 13 7-7 7 7" />
    </svg>
  )
}
export function IconArrowDown() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 4v14" />
      <path d="m5 11 7 7 7-7" />
    </svg>
  )
}
export function IconPencil() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 20h4l11-11-4-4L4 16v4Z" />
      <path d="m13.5 5.5 4 4" />
    </svg>
  )
}
export function IconTrash() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7h16" />
      <path d="M10 11v6M14 11v6" />
      <path d="M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7Z" />
      <path d="M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3" />
    </svg>
  )
}
export function IconSparkle() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
    </svg>
  )
}
export function IconSwap() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7h13l-3-3" />
      <path d="M20 17H7l3 3" />
    </svg>
  )
}
export function IconUpload() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 4v12" />
      <path d="M7 9l5-5 5 5" />
      <path d="M4 20h16" />
    </svg>
  )
}
export function IconLock({ size = 12 }: { size?: number } = {}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  )
}
export function IconChevronDown({ size = 12 }: { size?: number } = {}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  )
}
export function IconRefresh({ size = 14 }: { size?: number } = {}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  )
}
export function IconPlus({ size = 14 }: { size?: number } = {}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}
export function IconPaperclip({ size = 14 }: { size?: number } = {}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  )
}

// WaypointGlyph — single small symbol for a waypoint row, replacing the
// rounded-full chip pill used through R9. Picks an icon by either the
// system kind (start/end/highest/lowest/mid) or by the GPX <sym>; falls
// back to a generic pin. Colors are deliberately desaturated to fit the
// dense list without competing with the title text.
export type WaypointGlyphKind =
  | 'start'
  | 'end'
  | 'highest'
  | 'lowest'
  | 'mid'
  | 'generic'
  | 'photo'
  | 'parking'
  | 'rest_stop'
  | 'campsite'
  | 'hazard'
  | 'resupply'
  | 'viewpoint'
  | 'water_source'
  | 'trailhead'
  | 'junction'
  | 'shelter'

export function waypointGlyphKindForSym(sym?: string | null): WaypointGlyphKind {
  switch ((sym ?? '').trim()) {
    case 'Start':
      return 'start'
    case 'End':
      return 'end'
    case 'Summit':
      return 'highest'
    case 'Valley':
      return 'lowest'
    case 'XR-Mid':
      return 'mid'
    default:
      return 'generic'
  }
}

// Resolve the glyph kind for a waypoint, taking kind field and attachments into account.
// Priority: kind (if set and non-generic) > sym > hasAttachments > 'generic'.
// System materialization still only uses sym (handled by mergedWaypoints).
export function waypointGlyphKind(
  sym: string | null | undefined,
  hasAttachments: boolean,
  kind?: string | null,
): WaypointGlyphKind {
  if (kind && kind !== 'generic' && kind in WAYPOINT_GLYPH_INNER) {
    return kind as WaypointGlyphKind
  }
  const base = waypointGlyphKindForSym(sym)
  if (base === 'generic' && hasAttachments) return 'photo'
  return base
}

// Inner SVG markup for each glyph kind, shared between the React
// <WaypointGlyph> component (read/edit lists) and the map marker string
// builder so the side list and the map never drift. All shapes are
// single-color line/area art tinted via `currentColor` (outdoor "C" family,
// with the geometric valley line for `lowest`):
//   start    → waving flag on a pole
//   end      → checkered finish flag
//   mid      → centre dot on a horizontal line
//   highest  → mountain peak (snow cap)
//   lowest   → geometric valley line
//   generic  → location pin with a check
//   photo    → camera (generic waypoint with attachments)
const WAYPOINT_GLYPH_INNER: Record<WaypointGlyphKind, string> = {
  start: '<path d="M5 20V4" />' + '<path d="M5 5c3-2 6 2 9 0s5 0 5 0v6c-2 1-4-1-7 0s-7 0-7 0" />',
  end:
    '<path d="M5 20V4" />' +
    '<path d="M5 4h14v8H5z" />' +
    '<path fill="currentColor" stroke="none" d="M5 4h3.5v2.7H5zM12 4h3.5v2.7H12zM8.5 6.7H12v2.6H8.5zM15.5 6.7H19v2.6h-3.5z" />',
  mid: '<path d="M12 3v18" /><path d="M12 6l6 3-6 3z" fill="currentColor" stroke="none" />',
  highest:
    '<path d="M3 18l6-10 3 4 2-2.5 7 8.5z" />' +
    '<path d="M9 8l1.4 2.2-2.2 .7z" fill="currentColor" stroke="none" />',
  lowest: '<path d="M3 6l6 10 4-5 2 2.5 6-7" />',
  generic:
    '<path d="M12 21c4-5 6-8 6-11a6 6 0 1 0-12 0c0 3 2 6 6 11z" />' +
    '<path d="M9.5 10.5l1.8 1.8 3.2-3.4" />',
  photo: '<path d="M4 7h3l1.5-2h7L17 7h3v11H4z" /><circle cx="12" cy="12" r="3" />',
  // Route kinds
  parking:
    '<rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 16V8h4a3 3 0 0 1 0 6H9" />',
  rest_stop: '<circle cx="12" cy="12" r="8" /><path d="M12 8v4l3 3" />',
  resupply:
    '<path d="M6 2h12v6H6z" /><path d="M4 8h16v12H4z" /><path d="M9 8v12" /><path d="M15 8v12" />',
  // Universal kinds
  campsite: '<path d="M3 20l9-14 9 14z" /><path d="M12 6v0" />',
  hazard:
    '<path d="M12 3L2 21h20z" /><path d="M12 9v5" /><circle cx="12" cy="17" r="0.5" fill="currentColor" stroke="none" />',
  viewpoint:
    '<circle cx="12" cy="12" r="3" /><path d="M2 12h3" /><path d="M19 12h3" /><path d="M12 2v3" /><path d="M12 19v3" /><path d="M4.9 4.9l2.1 2.1" /><path d="M17 17l2.1 2.1" /><path d="M4.9 19.1l2.1-2.1" /><path d="M17 7l2.1-2.1" />',
  water_source: '<path d="M12 3c-3 4-6 7-6 10a6 6 0 0 0 12 0c0-3-3-6-6-10z" />',
  // Hiking kinds
  trailhead:
    '<path d="M3 21l6-6" /><path d="M9 15l6-6" /><path d="M15 9l6-6" /><circle cx="12" cy="12" r="2" />',
  junction:
    '<circle cx="12" cy="12" r="8" /><path d="M12 4v8" /><path d="M12 12l5.7 5.7" /><path d="M12 12l-5.7 5.7" />',
  shelter:
    '<path d="M3 12l9-8 9 8" /><path d="M5 12v8h14v-8" /><rect x="9" y="14" width="6" height="6" />',
}

export function WaypointGlyph({
  kind,
  size = 14,
  className,
}: {
  kind: WaypointGlyphKind
  size?: number
  className?: string
}) {
  // Use stroke="currentColor" so callers can tint per row via Tailwind text-*.
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      dangerouslySetInnerHTML={{ __html: WAYPOINT_GLYPH_INNER[kind] }}
    />
  )
}

// Build a standalone SVG string for a waypoint glyph, tinted to an
// explicit color. Used by RouteMap to render markers as plain DOM (Mapbox
// markers are imperative, outside React) while still matching the
// read-view glyph shapes exactly.
export function waypointGlyphSvgMarkup(
  kind: WaypointGlyphKind,
  size: number,
  color: string,
): string {
  return (
    `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" ` +
    `stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ` +
    `stroke-linejoin="round" aria-hidden="true" style="color:${color}">` +
    `${WAYPOINT_GLYPH_INNER[kind]}</svg>`
  )
}

export function waypointGlyphToneClass(_kind: WaypointGlyphKind): string {
  // Monochrome: every waypoint glyph renders in a single neutral tone.
  // The shape (flag / triangle / footprint / photo) carries the type
  // distinction, not color — so the map and the side list stay legible
  // and consistent across themes.
  return 'text-[var(--xr-text-secondary)]'
}
